import {EditPermissions} from '../../../../../../../shared/models';
import {UntypedFormArray, UntypedFormControl, UntypedFormGroup} from '@angular/forms';
import {getEditTypes} from '../../../delegatorUtils';
import {required} from '../../../../../../../shared/utils';
import {newStepValue} from '../../../../../services/flowBuilder/utils/constants';
import values from 'lodash/values';

export enum COLLAPSE_TOOLTIP_MESSAGE {
    EXPANDED = 'Expand all options',
    COLLAPSED = 'Collapse all options'
}

export enum COLLAPSE_GROUPS_TOOLTIP_MESSAGE {
    EXPANDED = 'Expand all groups',
    COLLAPSED = 'Collapse all groups'
}

export enum COLLAPSE_EXPAND_STATE {
    OPEN = 'open',
    CLOSE = 'close'
}

export enum COLLAPSE_EXPAND_GROUPS_STATE {
    OPEN = 'open',
    CLOSE = 'close'
}

export interface MultipleChoiceFormType {
    menuParams: any;
    menuOptions: any;
    menuGroups: any;
}

export class MultipleChoiceNode {
    menu: {} = {};

    constructor(nodeType: string) {
        this.menu = {
            'menuType': nodeType,
        };
    }

    static toForm(stepData: { [index: string]: any }): MultipleChoiceFormType {
        return {
            menuParams: this.menuParamsForm(stepData),
            menuOptions: this.menuOptionForm(stepData),
            menuGroups: this.menuGroupsForm(stepData)
        };
    }

    static menuParamsForm(stepData: { [index: string]: any }): {} {
        const menu = stepData?.menu;
        return {
            menuHeader: menu?.menuHeader ? menu.menuHeader : '',
            imageRef: menu?.imageRef ? menu.imageRef : '',
            bodyText: menu?.bodyText ? menu.bodyText : '',
            varName: menu?.input.varName ? menu.input.varName : '',
            optionSeperator: menu?.menuParams && menu?.menuParams.optionSeperator ? menu.menuParams.optionSeperator : '.',
            multipleSelection: menu?.menuParams && menu?.menuParams.multipleSelection ? menu.menuParams.multipleSelection : false,
            multipleGroups: menu?.menuParams && menu?.menuParams.multipleGroups ? menu.menuParams.multipleGroups : false,
            groupTitle: menu?.menuParams && menu?.menuParams.groupTitle ? menu.menuParams.groupTitle : '',
            displayType: menu?.menuParams && menu?.menuParams.displayType
                ? menu?.menuParams.displayType
                : menu?.menuParams && menu?.menuParams.displayTypes
                    ? menu?.menuParams.displayTypes
                    : {},
            next: stepData?.next ? stepData.next : newStepValue,
            default: menu?.results?.default.next ? menu?.results?.default.next : "OPTIONAL",
            customListButton: menu?.menuParams.customListButton,
        };
    }

    static checkIsOneGroupOnly(menu) {
        const groupsList = values(menu.groups);
        if (groupsList) {
            return groupsList.length === 1;
        }
        return false;
    }

    static menuOptionForm(stepData: { [index: string]: any }) {
        const menu = stepData?.menu;
        const menuOptions = {
            items: [],
            disabledItems: []
        };
        if (menu?.options) {
            const items = menu.options.items;
            for (const item in items) {
                const itemData: { [index: string]: any } = items[item];
                const itemFormData = {
                    text: itemData.text,
                    value: itemData.value,
                    imageRef: itemData.imageRef,
                    bodyText: itemData.bodyText,
                    keywords: menu.options.keywords ? menu.options.keywords[item] : []
                };
                menuOptions.items.push(itemFormData);
            }
        } else if (menu?.groups) {
          const menuGroup = menu.groups['0'];
          const items = this.getGroupItems(menuGroup.items);
          const disabledItems = this.getGroupItems(menuGroup.disabledItems);
          items.map(item => {
             menuOptions.items.push(item);
          });
          disabledItems.map(item => {
             menuOptions.disabledItems.push(item);
          });
        } else {
            menuOptions.items.push({
                text: '',
                value: '',
                imageRef: '',
                bodyText: '',
                keywords: []
            });
        }

        return menuOptions;
    }

    static menuGroupsForm(stepData: { [index: string]: any }): Object[] {
        const menu = stepData?.menu;
        const menuGroups: Object[] = [];
        if (menu?.groups) {
            const items = menu.groups;
            for (const item in items) {
                const itemData: { [index: string]: any } = items[item];
                const itemFormData = {
                    groupTitle: itemData.title,
                    multipleSelection: itemData.multipleSelection,
                    items: this.getGroupItems(itemData.items),
                    disabledItems: this.getGroupItems(itemData.disabledItems),
                };
                menuGroups.push(itemFormData);
            }
        } else {
            menuGroups.push({
                groupTitle: '',
                multipleSelection: false,
                items: [
                    {
                        text: '',
                        value: '',
                        imageRef: '',
                        bodyText: '',
                        keywords: []
                    }
                ],
                disabledItems: []
            });
        }
        return menuGroups;
    }

    static getGroupItems(groupItems) {
        const items: Object[] = [];
        for (const item in groupItems) {
            const itemData: { [index: string]: any } = groupItems[item];
            const itemFormData = {
                text: itemData.text,
                value: itemData.value,
                imageRef: itemData.imageRef,
                bodyText: itemData.bodyText,
                keywords: itemData.keywords
            };
            items.push(itemFormData);
        }
        return items;
    }

    static getOptionsTemplate(editPermissions: EditPermissions): UntypedFormGroup {
        const {editFlows} = getEditTypes(editPermissions);
        return new UntypedFormGroup({
            text: new UntypedFormControl({
                value: '',
                disabled: !editFlows
            }, [required('Field')]),
            value: new UntypedFormControl({
                value: '',
                disabled: !editFlows
            }),
            imageRef: new UntypedFormControl({
                value: '',
                disabled: !editFlows
            }),
            bodyText: new UntypedFormControl({
                value: '',
                disabled: !editFlows
            }), // [required('Body Text')]
            keywords: new UntypedFormArray([]) // [maxLength(5), hasDuplicates()]
        });
    }

    static getGroupTemplate(editPermissions: EditPermissions): UntypedFormGroup {
        const {editFlows} = getEditTypes(editPermissions);
        return new UntypedFormGroup({
            groupTitle: new UntypedFormControl({
                value: '',
                disabled: !editFlows
            }, [required('Field')]),
            multipleSelection: new UntypedFormControl({
                value: false,
                disabled: !editFlows
            }),
            items: new UntypedFormArray([this.getOptionsTemplate(editPermissions)]),
            disabledItems: new UntypedFormArray([])
        });
    }

    static getMenuTemplate(editPermissions: EditPermissions): UntypedFormGroup {

        const {editMenuTextOnly, editFlows} = getEditTypes(editPermissions);

        return new UntypedFormGroup({
            menuHeader: new UntypedFormControl({
                value: '',
                disabled: !editMenuTextOnly
            }),
            imageRef: new UntypedFormControl(''),
            bodyText: new UntypedFormControl({
                value: '',
                disabled: !editMenuTextOnly
            }, [required('Body text')]),
            varName: new UntypedFormControl({
                value: '',
                disabled: !editFlows
            }, [required('Variable Name')]),
            optionSeperator: new UntypedFormControl({
                value: '.',
                disabled: !editFlows
            }),
            multipleSelection: new UntypedFormControl(false),
            multipleGroups: new UntypedFormControl(false),
            groupTitle: new UntypedFormControl({
                value: '',
                disabled: !editMenuTextOnly
            }), // [required('Sub Header')]
            // displayType: new FormControl({
            //     value: 'auto',
            //     disabled: !editFlows
            // }, [required('WhatsApp Display Type')]),
            // displayType: new FormGroup({}, [required('WhatsApp Display Type')]),
            displayType: new UntypedFormGroup({}),
            customListButton: new UntypedFormControl(''),
            next: new UntypedFormControl(''),
            default: new UntypedFormControl(''),
        });
    }

    get nodeData(): {} {
        return {
            menu: this.menu,
            'type': 'menu',
            'next': newStepValue,
            metadata: {
                nodeVersion: '1.2.0',
            }
        };
    }

    addMenuDetails(menuHeader: string, imageRef: string, bodyText: string): void {
        this.menu = {
            ...this.menu,
            menuHeader,
            imageRef,
            bodyText
        };
    }

    addInput(varName: string): void {
        this.menu = {
            ...this.menu,
            input: {
                varName
            }
        };
    }

    addMenuParams(displayType: string, multipleGroups: boolean, optionSeperator: string, groupTitle: string, multipleSelection: boolean, customListButton: string, defaultNext: string): void {
        const menuParams = {
            multipleGroups,
            displayTypes: displayType,
            optionSeperator,
            groupTitle,
            multipleSelection,
            customListButton
        };
        this.menu = {
            ...this.menu,
            menuParams,
            results: {
                default: {
                    next: defaultNext
                }
            }
        };
    }

    addMenuOptions(options: {}, multipleSelection: boolean, groupTitle: string): void {
        this.menu = {
            ...this.menu,
            groups: this.getMenuOptions(options, multipleSelection, groupTitle)
        };
    }

    getMenuOptions(options, multipleSelection: boolean, groupTitle: string) {
        const items = this.mapOptions(options.items);
        const disabledItems = this.mapOptions(options.disabledItems);

        return {
          ['0']: {
            title: groupTitle,
            multipleSelection,
            items,
            disabledItems
          }
        };
    }

    mapOptions(options) {
        const items = [];
        for (const option in options) {
            let optionIndex = +option;
            const text = options[option]['text'];
            const value = options[option]['value'];
            const imageRef = options[option]['imageRef'];
            const bodyText = options[option]['bodyText'];
            const keywords = options[option]['keywords'];

            const index = ++optionIndex;

            items.push({
                text,
                value,
                imageRef,
                bodyText,
                keywords,
                order: index
            });
        }

        return items;
    }

    addMenuGroups(groupsList: [{}]): void {
        const groups = {};

        let orderCounter = 1;
        for (const group in groupsList) {
            const groupTitle = groupsList[group]['groupTitle'];
            const multipleSelection = groupsList[group]['multipleSelection'];
            const itemsArr = groupsList[group]['items'];
            const disabledItemsArr = groupsList[group]['disabledItems'];
            const items = [];
            const disabledItems = [];

            for (const i in itemsArr) {
              const item = itemsArr[i];
              item['order'] = orderCounter;
              items.push(item);
              orderCounter++;
            }

            for (const i in disabledItemsArr) {
                const item = disabledItemsArr[i];
                disabledItems.push(item);
            }

            groups[group] = {
                title: groupTitle,
                multipleSelection,
                items,
                disabledItems
            };
        }

        this.menu = {
            ...this.menu,
            groups
        };
    }

    getMenu() {
        return this.menu;
    }
}
